'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { Project, ProjectCreate, ProjectUpdate } from '@/shared/lib/platformApi';
import {
  useProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
} from '@/shared/hooks/useProjectsQuery';

interface ProjectContextType {
  projects: Project[];
  currentProject: Project | null;
  isLoading: boolean;
  /** True while a background refetch is in-flight. Distinct from
   *  `isLoading`, which also covers the initial load. Used by the
   *  Projects table to drive the refresh button's spinner. */
  isRefreshing: boolean;
  error: string | null;
  refreshProjects: () => void;
  setCurrentProject: (project: Project | null) => void;
  createProject: (projectData: ProjectCreate) => Promise<Project>;
  updateProject: (projectUUID: string, projectData: ProjectUpdate) => Promise<Project>;
  // 1. UPDATE INTERFACE: Add optional 'force' parameter
  deleteProject: (projectUUID: string, force?: boolean) => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

interface ProjectProviderProps {
  readonly children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const pathname = usePathname();

  // Use TanStack Query for projects
  const { data, isLoading, isFetching, error, refetch } = useProjectsQuery();
  const createMutation = useCreateProjectMutation();
  const updateMutation = useUpdateProjectMutation();
  const deleteMutation = useDeleteProjectMutation();

  const projects = data?.projects || [];

  // Extract project UUID from URL path
  const projectUUIDFromPath = React.useMemo(() => {
    try {
      if (!pathname) return null;
      const parts = pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('projects');
      if (idx !== -1 && parts.length > idx + 1) {
        return parts[idx + 1];
      }
      return null;
    } catch {
      return null;
    }
  }, [pathname]);

  // Auto-select project logic
  useEffect(() => {
    if (projects.length === 0) {
      // Not a no-op. A workspace with no projects would otherwise keep the
      // previous workspace's project in the chrome, naming something this
      // account cannot open.
      if (currentProject) setCurrentProject(null);
      return;
    }

    if (projectUUIDFromPath) {
      const byPath = projects.find(p => p.uuid === projectUUIDFromPath);
      if (byPath) {
        setCurrentProject(byPath);
        return;
      }
    }

    if (currentProject && projects.find(p => p.uuid === currentProject.uuid)) {
      return; 
    }

    setCurrentProject(projects[0]);
  }, [projects, projectUUIDFromPath, currentProject]);

  const createProject = async (projectData: ProjectCreate): Promise<Project> => {
    const newProject = await createMutation.mutateAsync(projectData);
    setCurrentProject(newProject);
    return newProject;
  };

  const updateProject = async (projectUUID: string, projectData: ProjectUpdate): Promise<Project> => {
    const updatedProject = await updateMutation.mutateAsync({ projectUUID, projectData });
    
    if (currentProject?.uuid === projectUUID) {
      setCurrentProject(updatedProject);
    }
    
    return updatedProject;
  };

  // 2. IMPLEMENTATION: Add optional 'force' parameter
  const deleteProject = async (projectUUID: string, force: boolean = false): Promise<void> => {
    // Pass an object { uuid, force }
    await deleteMutation.mutateAsync({ uuid: projectUUID, force });
    
    if (currentProject?.uuid === projectUUID) {
      const remainingProjects = projects.filter(p => p.uuid !== projectUUID);
      setCurrentProject(remainingProjects.length > 0 ? remainingProjects[0] : null);
    }
  };

  const value = React.useMemo<ProjectContextType>(
    () => ({
      projects,
      currentProject,
      isLoading: isLoading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
      isRefreshing: isFetching,
      error: error?.message || null,
      refreshProjects: () => refetch(),
      setCurrentProject,
      createProject,
      updateProject,
      deleteProject,
    }),
    [
      projects,
      currentProject,
      isLoading,
      isFetching,
      createMutation.isPending,
      updateMutation.isPending,
      deleteMutation.isPending,
      error,
      refetch,
      setCurrentProject,
      createProject,
      updateProject,
      deleteProject,
    ]
  );

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};